import { Directive, OnDestroy, OnInit } from '@angular/core';
import { Client } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { Subject, Subscription, merge } from 'rxjs';

export enum ZippyEventType {
    open,
    change,
    close,
    toggle,
    openAny,
    isOpen,
    isAnyOpen
}

export interface IZippyEvent {
    type: ZippyEventType;
    id: IZippyId;
    stopped?: boolean;
}

export interface IZippyChangeEvent extends IZippyEvent {
    visible: boolean;
}

export interface IZippyId {
    name: string;
    group?: string;
    isGroup: boolean;
}

export const subject = new Subject<IZippyEvent | IZippyChangeEvent>();

export function parseZippyName(name: string | (string | number)[]): IZippyId {
    // this method is greedy - if you change to null name remains - this is to avoid problems with multiple name attrs
    if (typeof name === 'string') {
        name = name.split('.');
    }

    // clone immutablei
    const names = [...name];

    const _name = `${names.pop()}`; // last element is name
    const isGroup = !names.length;
    const group = !isGroup ? names.join('.') : _name; // all beginning ones are group

    return {
        name: _name,
        group,
        isGroup
    };
}

@Directive()
export abstract class ZippyAbstract implements OnInit, OnDestroy {
    protected subscriptions: Subscription = new Subscription();

    @NgInject(Client) readonly client!: Client;
    @NgInject(Events) readonly events!: Events;

    @NgInject(TimeoutService)
    protected timeoutService!: TimeoutService;

    /**
     * Identifier of zippy element
     * @type {IZippyId}
     */
    protected id!: IZippyId;

    /**
     * Subject for events passing
     * @type {[type]}
     */
    protected subject = subject;

    /**
     * On init hook
     * subscribes to events
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    ngOnInit() {
        this.subscriptions.add(merge(this.subject, this.events.on('zippy.event')).subscribe((event: IZippyEvent) => this.onEvent(event)));
    }

    /**
     * On destroy hook
     * unsubscribes
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    /**
     * Converts name string or name array into id
     *
     * Accepted formats exmples
     *
     * someGroup.someName
     * some.group.someName - last part dot separated is name
     * [someGroup, someName]
     * ['some', 'group', 'someName'] - last part dot separated is name
     *
     * Number of pieces is not limited
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {string | string[]} name
     */
    protected parseName(name: string | (string | number)[]) {
        // this method is greedy - if you change to null name remains - this is to avoid problems with multiple name attrs
        if (name) {
            this.id = parseZippyName(name);
        }
    }

    /**
     * Checks if event target id matches this zippy id exactly.
     *
     * @param  {IZippyEvent} event
     * @return {boolean}
     */
    protected checkEventMatchExact(event: IZippyEvent): boolean {
        return this.id && event.id && event.id.name === this.id.name && event.id.group === this.id.group;
    }

    /**
     * Checks if event target is of same group only but is not exactly same
     * @param  {IZippyEvent} event
     * @return {boolean}
     */
    protected checkEventMatchGroup(event: IZippyEvent): boolean {
        return this.id && event.id && event.id.group === this.id.group;
    }

    /**
     * Checks if any from the group is open
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    protected isAnyOpen(): boolean {
        const event: IZippyEvent = {
            id: this.id,
            type: ZippyEventType.isAnyOpen
        };

        this.subject.next(event);

        return !!event.stopped;
    }

    /**
     * Checks if item is open
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    protected isOpen(): boolean {
        const event: IZippyEvent = {
            id: this.id,
            type: ZippyEventType.isOpen
        };
        this.subject.next(event);

        return !!event.stopped;
    }
    /**
     * Events listener
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {IZippyEvent} event
     */
    protected abstract onEvent(event: IZippyEvent);
}
